/**
 * Alaska Gift — Cart Drawer
 * Slide-out cart panel with AJAX updates via Section Rendering API.
 * Depends on: global.js (AlaskaUtils)
 */

(() => {
  const SECTION_ID = 'cart-drawer';
  const routes = window.routes || {};
  const rootUrl = routes.root_url || '/';
  const drawer = document.querySelector('[data-cart-drawer]');
  const overlay = document.querySelector('[data-cart-overlay]');
  if (!drawer) return;

  /* ── Helpers ────────────────────────────────────────────── */

  let lastFocusedEl = null;

  let savedScrollY = 0;

  function open() {
    lastFocusedEl = document.activeElement;
    savedScrollY = window.scrollY;
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-open');
    document.body.style.top = -savedScrollY + 'px';
    AlaskaUtils.trapFocus(drawer);
    drawer.querySelector('[data-cart-drawer-close]')?.focus();
  }

  function close() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.style.top = '';
    window.scrollTo(0, savedScrollY);
    AlaskaUtils.removeTrapFocus(drawer);
    if (lastFocusedEl) lastFocusedEl.focus();
  }

  /* ── Section Rendering: swap server-rendered HTML ──────── */

  function updateDrawerHTML(sectionHtml) {
    const tmp = document.createElement('div');
    tmp.innerHTML = sectionHtml;

    // Replace items
    const newItems = tmp.querySelector('[data-cart-drawer-items]');
    const oldItems = drawer.querySelector('[data-cart-drawer-items]');
    if (newItems && oldItems) oldItems.innerHTML = newItems.innerHTML;

    // Replace empty state
    const newEmpty = tmp.querySelector('[data-cart-drawer-empty]');
    const oldEmpty = drawer.querySelector('[data-cart-drawer-empty]');
    if (newEmpty && oldEmpty) oldEmpty.className = newEmpty.className;

    // Replace view-cart link
    const newViewCart = tmp.querySelector('[data-cart-drawer-view-cart]');
    const oldViewCart = drawer.querySelector('[data-cart-drawer-view-cart]');
    if (newViewCart && oldViewCart) oldViewCart.className = newViewCart.className;

    // Replace footer
    const newFooter = tmp.querySelector('[data-cart-drawer-footer]');
    const oldFooter = drawer.querySelector('[data-cart-drawer-footer]');
    if (newFooter && oldFooter) {
      oldFooter.className = newFooter.className;
      oldFooter.innerHTML = newFooter.innerHTML;
    }

    // Update count badge in drawer
    const newCount = tmp.querySelector('[data-cart-drawer-count]');
    const oldCount = drawer.querySelector('[data-cart-drawer-count]');
    if (newCount && oldCount) oldCount.textContent = newCount.textContent;

    // Update all site-wide count badges
    const count = newCount?.textContent || '0';
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
    });

    // Announce cart update to screen readers
    const status = drawer.querySelector('[data-cart-drawer-status]');
    if (status) status.textContent = `Cart updated. ${count} item${count === '1' ? '' : 's'} in cart.`;
  }

  /* ── Cart API with Section Rendering ─────────────────── */

  let activeController = null;

  async function fetchAndRender() {
    if (activeController) activeController.abort();
    activeController = new AbortController();

    try {
      const res = await fetch(`${rootUrl}cart?sections=${SECTION_ID}`, {
        signal: activeController.signal,
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) throw new Error('Cart fetch failed');
      const data = await res.json();
      if (data[SECTION_ID]) {
        updateDrawerHTML(data[SECTION_ID]);
      }
      return data;
    } catch (err) {
      if (err.name !== 'AbortError') showCartError();
    } finally {
      activeController = null;
    }
  }

  function showCartError(message) {
    const existing = drawer.querySelector('.cart-drawer__error');
    if (existing) existing.remove();
    const msg = document.createElement('div');
    msg.className = 'cart-drawer__error';
    msg.setAttribute('role', 'alert');
    msg.textContent = message || 'Something went wrong. Please try again.';
    drawer.querySelector('.cart-drawer__header')?.after(msg);
    setTimeout(() => msg.remove(), 4000);
  }

  /* ── Debounced quantity changes ─────────────────────── */

  const debounceTimers = {};
  function debouncedChange(key, quantity, itemEl) {
    if (itemEl) {
      const qtyEl = itemEl.querySelector('[data-cart-qty-value]');
      if (quantity > 0 && qtyEl) qtyEl.textContent = quantity;
      if (quantity === 0) itemEl.classList.add('is-removing');
      itemEl.querySelectorAll('button').forEach(b => b.disabled = true);
    }

    clearTimeout(debounceTimers[key]);
    debounceTimers[key] = setTimeout(() => changeQuantity(key, quantity), 300);
  }

  async function changeQuantity(key, quantity) {
    if (activeController) activeController.abort();
    activeController = new AbortController();

    try {
      const res = await fetch(`${rootUrl}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: SECTION_ID }),
        signal: activeController.signal
      });
      if (!res.ok) throw new Error('Cart change failed');
      const data = await res.json();
      if (data.sections?.[SECTION_ID]) {
        updateDrawerHTML(data.sections[SECTION_ID]);
      }
    } catch (err) {
      if (err.name !== 'AbortError') await fetchAndRender();
    } finally {
      activeController = null;
    }
  }

  /* ── Event delegation for dynamic items ───────────────── */

  drawer.addEventListener('click', (e) => {
    const item = e.target.closest('[data-cart-drawer-item]');
    if (!item) return;
    const key = item.dataset.key || item.dataset.variantId;
    if (!key) return;

    if (e.target.closest('[data-cart-qty-minus]')) {
      const qtyEl = item.querySelector('[data-cart-qty-value]');
      const qty = parseInt(qtyEl.textContent, 10);
      debouncedChange(key, qty > 1 ? qty - 1 : 0, item);
    }

    if (e.target.closest('[data-cart-qty-plus]')) {
      const qtyEl = item.querySelector('[data-cart-qty-value]');
      const qty = parseInt(qtyEl.textContent, 10);
      debouncedChange(key, qty + 1, item);
    }

    if (e.target.closest('[data-cart-remove]')) {
      debouncedChange(key, 0, item);
    }
  });

  /* ── Open / close triggers ─────────────────────────────── */

  document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      open();
      fetchAndRender();
    });
  });

  document.querySelectorAll('[data-cart-drawer-close]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      if (btn.tagName !== 'A') e.preventDefault();
      close();
    });
  });

  overlay.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  /* ── Global API: open drawer after add-to-cart ─────────── */

  window.AlaskaCart = {
    open,
    close,
    refresh: fetchAndRender,
    async addAndOpen(variantId, quantity = 1) {
      if (activeController) activeController.abort();
      activeController = new AbortController();
      try {
        const res = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity }],
            sections: SECTION_ID
          }),
          signal: activeController.signal
        });
        if (!res.ok) {
          // Shopify returns a JSON body with a human-readable reason on 422
          // (e.g. "All 3 Foo are in your cart." or an out-of-stock message).
          let reason = '';
          try {
            const errData = await res.json();
            reason = errData.description || errData.message || '';
          } catch (_) { /* non-JSON body — fall back to generic message */ }
          const addError = new Error(reason || 'Add to cart failed');
          addError.userMessage = reason;
          throw addError;
        }
        const data = await res.json();
        open();
        if (data.sections?.[SECTION_ID]) {
          updateDrawerHTML(data.sections[SECTION_ID]);
        } else {
          await fetchAndRender();
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        open();
        showCartError(err.userMessage);
        await fetchAndRender();
        throw err;
      } finally {
        activeController = null;
      }
    }
  };
})();
